import 'leaflet';
import 'leaflet.markercluster';

import {
  AfterViewInit,
  Component,
  OnDestroy,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
//import { GestureHandling } from 'leaflet-gesture-handling';
import { UiService } from '../../../services/ui.service';
import { I18nService } from '../../../services/i18n.service';
import { AssetsService } from '../../../services/asset.service';
import { SpinnerComponent } from '../../spinner/spinner';
import { LP_UI_CONFIG } from '../../../core/tokens';
import { GLOBAL_CONSTANT } from '../../../constants/global.constant';
import { escapeHtml } from '../../../utilities/global.util';

const M = (window as any)['L'];

@Component({
  selector: 'app-read-only-map',
  templateUrl: './read-only-map.html',
  styleUrls: ['./read-only-map.scss'],
  imports: [SpinnerComponent],
})
export class ReadOnlyMapComponent implements AfterViewInit, OnDestroy {
  private readonly cfg = inject(LP_UI_CONFIG);
  private readonly ui = inject(UiService);
  public readonly i18n = inject(I18nService);
  private readonly assetsService = inject(AssetsService);

  /** ===== Signal Inputs / Outputs ===== */
  /** Current sensor position (required by parent) */
  SensorPosition = input<{ lat: number; lng: number } | null>(null);
  /** Label shown on the marker */
  SensorLabel = input<string>('');
  /** Parent-provided loading state (optional) */
  Loading = input<boolean>(false);

  /** Emits when the map has initialized */
  mapReady = output<void>();
  /** Emits when the view (center/zoom) changes */
  viewChanged = output<{ center: [number, number]; zoom: number }>();

  /** ===== Internal state (signals) ===== */
  isLoading = signal<boolean>(false);

  zoomLevel = signal<number>(13);
  satelliteView = signal<boolean>(true);
  center = signal<[number, number]>([51.175545, 10.2180081]);

  /** Leaflet references (not signals) */
  Map: any = null;
  tileLayer: any = null;
  markersLayer: any = null;

  /** A dedicated marker for the sensor */
  safeLabel = escapeHtml(this.SensorLabel() ?? '');
  addedMarker = M.marker(this.center(), {
    icon: M.divIcon({
      className: 'sensor-marker',
      html: `
        <div class="sub-marker body-bold">
          <span>${this.safeLabel}</span>
        </div>`,
      iconSize: [56, 68],
      iconAnchor: [28, 68],
    }),
  });

  /** Derived (computed) */
  isMobile = computed(() => this.ui.IsMobile());

  private readonly _posEffect = effect(() => {
    const pos = this.SensorPosition();
    // Only act when map is ready
    if (pos && this.Map) {
      this.center.set([pos.lat, pos.lng]);
      this.addMarkers();
    }
  });

  private readonly _labelEffect = effect(() => {
    const label = this.SensorLabel() ?? GLOBAL_CONSTANT.EMPTY_STRING;
    if (this.Map) {
      this.updateAddedMarkerLabel(label);
      this.addMarkers();
    }
  });

  private readonly _loadingEffect = effect(() => {
    this.isLoading.set(this.Loading());
    if (this.Map) {
      this.addMarkers();
    }
  });

  ngAfterViewInit() {
    this.initMap();
  }

  ngOnDestroy() {
    if (this.Map) {
      this.Map.off();
      this.Map.remove();
    }
  }

  /** ===== Map setup ===== */
  private async initMap(): Promise<void> {
    if (this.Map) {
      this.Map.off();
      this.Map.remove();
    }

    // enable gesture handling
    M.Map.addInitHook('addHandler', 'gestureHandling');

    // fix default icon shadow resolution issues
    delete M.Icon.Default.prototype._getIconUrl;

    this.Map = M.map('ReadOnlyMap', {
      zoomControl: false,
      gestureHandling: true,
      gestureHandlingOptions: {
        text: {
          touch: this.i18n.langString('map_gesture_touch'),
          scroll: this.i18n.langString('map_gesture_scroll'),
          scrollMac: this.i18n.langString('map_gesture_scroll_mac'),
        },
        duration: 1500,
      },
    });

    // Move end emits viewChanged output
    this.Map.on('moveend', () => {
      const c = this.Map.getCenter(); // returns LatLng
      this.viewChanged.emit({
        center: [c.lat, c.lng],
        zoom: this.Map.getZoom(),
      });
    });

    // Prefer Promises; if your service still returns Observable, wrap with firstValueFrom
    try {
      const keys = await this.assetsService.getMapBoxKeys();
      //const keys = this.cfg.mapbox;
      this.tileLayer = null;
      this.addTiles(keys);
      this.addMarkers();

      setTimeout(() => {
        this.Map.invalidateSize();
        const ctr = this.center();
        if (ctr[0] && ctr[1]) {
          this.Map.setView(ctr, this.zoomLevel());
        }
        this.mapReady.emit();
      }, 500);
    } catch (err) {
      // handle gracefully; optional: set a fallback tile layer
      console.error('Failed to load MapBox keys', err);
    }
  }

  private async addTiles(mapBoxKeys: any) {
    if (this.tileLayer) this.Map.removeLayer(this.tileLayer);
    const keys = await this.assetsService.getMapBoxKeys();
    const map_access = this.satelliteView() ? keys.satelliteView : keys.mapView;

    this.tileLayer = M.tileLayer(map_access, {
      attribution:
        'Map data &copy; https://www.openstreetmap.org/OpenStreetMap</a> contributors, ' +
        'https://creativecommons.org/licenses/by-sa/2.0/CC-BY-SA</a>, Imagery © https://www.mapbox.com/Mapbox</a>',
      maxZoom: 18,
      id: 'mapbox/streets-v11',
      tileSize: 512,
      zoomOffset: -1,
      boxZoom: true,
      zoomAnimation: true,
      accessToken: mapBoxKeys.geoCodingToken,
    });

    this.tileLayer.addTo(this.Map);
  }

  private addMarkers() {
    if (!this.Map) return;

    // create/reset layer group
    if (this.markersLayer) this.Map.removeLayer(this.markersLayer);
    this.markersLayer = M.layerGroup({});

    // update marker position
    const ctr = this.center();
    this.addedMarker.setLatLng({ lat: ctr[0], lng: ctr[1] });

    // add the marker to layer group
    this.markersLayer.addLayer(this.addedMarker);

    // add layer group to map
    this.Map.addLayer(this.markersLayer);

    // center view (if valid coords)
    if (ctr[0] && ctr[1]) {
      this.Map.setView(ctr, this.zoomLevel());
    }
  }

  private updateAddedMarkerLabel(label: string) {
    const safeLabel = escapeHtml(label);
    const html = `
      <div class="sub-marker body-bold">
        <span ${safeLabel.length > 3 ? 'class="smallFont"' : ''}>
          ${safeLabel}
        </span>
      </div>
    `;
    // If marker already exists, just update icon; if not, create it
    if (this.addedMarker) {
      this.addedMarker.setIcon(
        M.divIcon({
          className: 'sensor-marker',
          html,
          iconSize: [56, 68],
          iconAnchor: [28, 68],
        })
      );
    } else {
      this.addedMarker = M.marker(this.center(), {
        icon: M.divIcon({
          className: 'sensor-marker',
          html,
          iconSize: [56, 68],
          iconAnchor: [28, 68],
        }),
      });
    }
  }
}
